import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Factory, QrCode, LogOut, Camera } from 'lucide-react';
import { ScannerQR } from '@/components/ScannerQR';

export function ModoFabrica() {
  const navigate = useNavigate();
  const { loginOperador, isLoading, error, user, isAuthenticated, logout } = useAuth();
  const [matricula, setMatricula] = useState('');
  const [pin, setPin] = useState('');
  const [showScanner, setShowScanner] = useState(false);

  const handleScan = (decodedText: string) => {
    // Tenta extrair o ID da URL do QR Code
    // Ex: https://fort3d.com.br/modo-fabrica/apontar?id=UUID
    const url = new URL(decodedText);
    const id = url.searchParams.get('id');
    if (id) {
      navigate(`/modo-fabrica/apontamento?id=${id}`);
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    loginOperador({ matricula, pin });
  };

  // Se já está logado, mostra dashboard do operador
  if (isAuthenticated && user?.role === 'OPERADOR') {
    return (
      <div className="min-h-screen bg-fort3d-preto p-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 pt-4">
            <div className="flex items-center gap-2">
              <Factory className="h-6 w-6 text-fort3d-laranja" />
              <span className="text-xl font-bold text-white">Modo Fábrica</span>
            </div>
            <button onClick={logout} className="text-fort3d-cinza-claro hover:text-white">
              <LogOut className="h-5 w-5" />
            </button>
          </div>

          {/* Boas vindas */}
          <Card className="mb-6 border-fort3d-ciano/30">
            <CardContent className="p-6 text-center">
              <p className="text-fort3d-cinza-claro mb-1">Bem-vindo,</p>
              <h1 className="text-2xl font-bold text-white">{user.nome}</h1>
              <p className="text-fort3d-ciano font-mono mt-2">{user.matricula}</p>
            </CardContent>
          </Card>

          {/* Ações */}
          <div className="space-y-4">
            <button
              onClick={() => setShowScanner(true)}
              className="w-full p-8 rounded-2xl bg-gradient-to-br from-fort3d-laranja to-[#FF8C38] text-white flex flex-col items-center justify-center gap-4 border-2 border-white/10 shadow-lg active:scale-95 transition-all"
            >
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-md">
                <Camera className="h-8 w-8" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-black mb-1">Escanear QR</h2>
                <p className="text-white/70 text-sm">Aponte para a ficha de produção</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/modo-fabrica/impressao')}
              className="w-full p-6 rounded-xl bg-fort3d-laranja text-white text-left hover:bg-fort3d-laranja/90 transition-colors"
            >
              <h2 className="text-xl font-bold mb-1">Impressão</h2>
              <p className="text-white/80 text-sm">Iniciar ou pausar impressões</p>
            </button>

            <button
              onClick={() => navigate('/modo-fabrica/qualidade')}
              className="w-full p-6 rounded-xl bg-fort3d-ciano text-fort3d-preto text-left hover:bg-fort3d-ciano/90 transition-colors"
            >
              <h2 className="text-xl font-bold mb-1">Qualidade</h2>
              <p className="text-fort3d-preto/80 text-sm">Checklist e fotos</p>
            </button>

            <button
              onClick={() => navigate('/modo-fabrica/estoque')}
              className="w-full p-6 rounded-xl bg-fort3d-cinza-escuro border border-fort3d-cinza-medio text-white text-left hover:bg-fort3d-cinza-medio transition-colors"
            >
              <h2 className="text-xl font-bold mb-1">Estoque</h2>
              <p className="text-fort3d-cinza-claro text-sm">Consultar insumos</p>
            </button>
          </div>
          
          {showScanner && (
            <ScannerQR onScan={handleScan} onClose={() => setShowScanner(false)} />
          )}
        </div>
      </div>
    );
  }

  // Tela de login
  return (
    <div className="min-h-screen bg-fort3d-preto flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border-fort3d-cinza-medio">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-fort3d-ciano/10">
            <Factory className="h-8 w-8 text-fort3d-ciano" />
          </div>
          <CardTitle className="text-2xl text-white">Modo Fábrica</CardTitle>
          <p className="text-fort3d-cinza-claro text-sm">Acesso para Operadores</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              label="Matrícula"
              value={matricula}
              onChange={(e) => setMatricula(e.target.value)}
              placeholder="OP001"
              required
            />
            <Input
              label="PIN (4 dígitos)"
              type="password"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="••••"
              required
            />

            {error && (
              <div className="text-sm text-fort3d-vermelho text-center">
                Matrícula ou PIN inválidos
              </div>
            )}

            <Button
              type="submit"
              variant="nuvem"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              Entrar
            </Button>

            <div className="flex items-center justify-center gap-2 mt-4">
              <QrCode className="h-5 w-5 text-fort3d-cinza-claro" />
              <span className="text-sm text-fort3d-cinza-claro">Ou escaneie o QR da máquina</span>
            </div>
          </form>

          <div className="mt-6 text-center">
            <a href="/admin/login" className="text-sm text-fort3d-cinza-claro hover:text-white transition-colors">
              Acesso Administrativo →
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
